import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { DataService } from '@app-core/services/data/data.service';
import { DRIVER_UPDATION_LOG_TABLE_COLUMN, UPDATION_LOG_CSV_HEADERS } from '@app-trips/common/trips.constants';
import {
  DriverUpdateRequestStatus,
  DriverUpdateRequestType,
  UpdationLogsParams,
  UpdationLogsResponse,
  UpdationLogsResponseRowResult,
  UpdationLogsResponseRows,
} from '@app-trips/common/trips.model';
import { TripsService } from '@app-trips/services/trips.service';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
@Component({
  selector: 'app-bulk-updation-log',
  templateUrl: './bulk-updation-log.component.html',
  styleUrls: ['./bulk-updation-log.component.scss'],
})
export class BulkUpdationLogComponent implements OnInit, AfterViewInit {
  @ViewChild('paginator', { static: true }) public paginator: MatPaginator;

  public Status = DriverUpdateRequestStatus;
  public displayedColumns: string[] = DRIVER_UPDATION_LOG_TABLE_COLUMN;
  public pageSize: number = 10;
  private skip = 0;
  public logsCount: number = 0;

  private fleetId: string;
  public tableColumns;
  public tableSource: MatTableDataSource<any> = new MatTableDataSource([]);
  public loader: boolean = false;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(private tripsService: TripsService, private dataService: DataService, private mixpanelService: MixPanelService) {}

  ngOnInit(): void {
    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
      if (value) {
        this.fleetId = value;
      }
      this.getDriverUpdationLogs();
    });
  }

  ngAfterViewInit(): void {
    this.paginator.page.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: PageEvent) => {
      this.skip = event.pageIndex * this.pageSize;
      this.getDriverUpdationLogs();
    });
  }

  public getDriverUpdationLogs() {
    this.loader = true;
    this.tableSource.data = new Array(this.pageSize).fill(undefined);

    const params: UpdationLogsParams = {
      'updateRequestTypes[]': [DriverUpdateRequestType.BulkAssetTrips, DriverUpdateRequestType.AssetTrip],
      limit: this.pageSize,
      offset: this.skip,
    };
    this.tripsService
      .getDriverUpdationLogs(params)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: UpdationLogsResponse) => {
          let data = res?.rows || [];
          this.tableSource.data = data;
          this.logsCount = res?.totalCount || 0;
        },
        () => {
          this.tableSource.data = [];
          this.logsCount = 0;
        }
      );
  }

  public downloadLogs(row: UpdationLogsResponseRows) {
    const result: UpdationLogsResponseRowResult[] = row?.result || [];
    const csvData = this.convertToCSV(result);
    const fileName = `${this.fleetId}_${row?.updateRequestId}_driver_updation_logs`;
    this.mixpanelService.track('Download Logs Clicked - Manage Tab /Trips Page', {
      'Fleet Id': this.fleetId,
      'Update Request Id': row?.updateRequestId,
    });
    this.tripsService.downloadFile(csvData, fileName, 'text/csv;charset=utf-8;');
  }

  private convertToCSV(result: UpdationLogsResponseRowResult[]): string {
    const header: string[] = UPDATION_LOG_CSV_HEADERS.map((header) => `"${header.label}"`);
    const keys: string[] = UPDATION_LOG_CSV_HEADERS.map((header) => header.value);

    const data: string[] =
      result?.map((row) => {
        const { updatedTripsCount, ignoredTripsCount, totalTripsCount } = this.calculateTripsCount(row);

        const rowValue = keys.map((key) => {
          switch (key) {
            case 'totalTrips':
              return `"${totalTripsCount}"`;

            case 'updatedTrips':
              return `"${updatedTripsCount}"`;

            case 'ignoredTrips':
              return `"${ignoredTripsCount}"`;

            case 'comments':
              return `"${this.getComments(row)}"`;

            default:
              return `"${row[key] ?? ''}"`;
          }
        });

        return rowValue.join(',');
      }) || [];
    const csv = [header.join(','), ...data];
    return csv.join('\n');
  }

  private calculateTripsCount(row: UpdationLogsResponseRowResult) {
    let updatedTripsCount, ignoredTripsCount, totalTripsCount;

    if (row.hasOwnProperty('code')) {
      updatedTripsCount = ignoredTripsCount = totalTripsCount = 'N/A';
    } else {
      updatedTripsCount = this.getUpdatedTripsCount(row);
      ignoredTripsCount = this.getIgnoredTripsCount(row);
      totalTripsCount = updatedTripsCount + ignoredTripsCount;
    }

    return { updatedTripsCount, ignoredTripsCount, totalTripsCount };
  }

  private getUpdatedTripsCount(row: UpdationLogsResponseRowResult) {
    return row?.affectedTripIds?.length || 0;
  }

  private getIgnoredTripsCount(row: UpdationLogsResponseRowResult) {
    const count = row?.ignoredTripIds?.reduce((count, ignoredTrips) => {
      const len = ignoredTrips.tripIds?.length || 0;
      return count + len;
    }, 0);
    return count || 0;
  }

  private getComments(row: UpdationLogsResponseRowResult) {
    if (row.hasOwnProperty('code')) {
      return row.value?.message;
    }
    const comments = row?.ignoredTripIds
      ?.filter((ignoredTrips) => ignoredTrips.tripIds?.length > 0)
      .map((ignoredTrips) => ignoredTrips.reason);

    return comments ? comments.join(' | ') : '';
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
